import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";

export default function Method() {
  const { t } = useLanguage();
  usePageTitle(t.meta.method);
  const m = t.method;

  return (
    <div data-testid="method-page" className="pt-24">
      <section className="border-b border-coal bg-ink">
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-20 md:px-10 md:pt-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
              <span className="h-px w-10 bg-bronze" />
              {m.overline}
            </p>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[0.95] tracking-tight text-ivory md:text-6xl lg:text-7xl">
              {m.heading}
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/60 md:text-lg">{m.sub}</p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
        {m.steps.map((s, i) => (
          <Reveal key={i}>
            <div
              data-testid={`method-stage-${i}`}
              className="grid gap-8 border-t border-line py-14 first:border-t-0 first:pt-0 md:grid-cols-12 md:gap-10"
            >
              <div className="md:col-span-3">
                <span className="font-serif text-7xl leading-none text-bronze/90 md:text-8xl">{s.letter}</span>
                <p className="mt-3 text-xs uppercase tracking-[0.25em] text-stone">
                  0{i + 1} / 07
                </p>
              </div>
              <div className="md:col-span-5">
                <h2 className="font-serif text-3xl text-ink md:text-4xl">{s.title}</h2>
                <p className="mt-4 text-base leading-relaxed text-stone md:text-lg">{s.lead}</p>
                <ul className="mt-6 space-y-3">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex gap-3 text-sm leading-relaxed text-ink/90 md:text-base">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-bronze/40 bg-white">
                        <Check className="h-3 w-3 text-bronze" />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:col-span-4">
                <div className="border border-line bg-sand p-7 md:sticky md:top-28">
                  <p className="text-xs uppercase tracking-[0.2em] text-bronze">{m.resultLabel}</p>
                  <p className="mt-3 font-serif text-xl leading-snug text-ink md:text-2xl">{s.outcome}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <CTASection />
    </div>
  );
}
